/**
 * AAAprinters - Shopping Cart React Hook
 * Manages cart state with Supabase integration and localStorage fallback
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CartItem {
  id?: string;
  product_id: string;
  quantity: number;
  size?: string;
  color?: string;
  custom_design?: any;
  created_at?: string;
  updated_at?: string;
}

interface UseCartReturn {
  cart: CartItem[];
  cartCount: number;
  loading: boolean;
  error: string | null;
  addToCart: (item: Omit<CartItem, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateQuantity: (productId: string, quantity: number, size?: string, color?: string) => Promise<void>;
  removeFromCart: (productId: string, size?: string, color?: string) => Promise<void>;
  clearCart: () => Promise<void>;
  syncCart: () => Promise<void>;
}

const CART_STORAGE_KEY = 'aaaprinters_cart';

/**
 * Hook for managing shopping cart state
 */
export function useCart(): UseCartReturn {
  const { user, isAuthenticated } = useAuth();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load cart from localStorage
  const loadLocalCart = useCallback((): CartItem[] => {
    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.error('Error loading local cart:', err);
      return [];
    }
  }, []);

  // Save cart to localStorage
  const saveLocalCart = useCallback((items: CartItem[]) => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } catch (err) {
      console.error('Error saving local cart:', err);
    }
  }, []);

  // Fetch cart from Supabase (authenticated users)
  const fetchCart = useCallback(async () => {
    if (!user) {
      // Load from localStorage for anonymous users
      const localCart = loadLocalCart();
      setCart(localCart);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('carts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setCart(data || []);
    } catch (err) {
      console.error('Error fetching cart:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch cart');
      // Fallback to local cart
      const localCart = loadLocalCart();
      setCart(localCart);
    } finally {
      setLoading(false);
    }
  }, [user, loadLocalCart]);

  // Sync local cart to Supabase when user logs in
  const syncCart = useCallback(async () => {
    if (!user) return;

    const localCart = loadLocalCart();
    if (localCart.length === 0) return;

    setLoading(true);

    try {
      // Insert local cart items to Supabase
      const itemsToSync = localCart.map(item => ({
        user_id: user.id,
        product_id: item.product_id,
        quantity: item.quantity,
        size: item.size,
        color: item.color,
        custom_design: item.custom_design,
      }));

      const { error: syncError } = await supabase
        .from('carts')
        .upsert(itemsToSync, {
          onConflict: 'user_id,product_id,size,color',
        });

      if (syncError) throw syncError;

      // Clear local cart after successful sync
      localStorage.removeItem(CART_STORAGE_KEY);

      // Fetch updated cart
      await fetchCart();
    } catch (err) {
      console.error('Error syncing cart:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync cart');
    } finally {
      setLoading(false);
    }
  }, [user, loadLocalCart, fetchCart]);

  // Load cart on mount and when user changes
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Sync cart when user logs in
  useEffect(() => {
    if (user) {
      syncCart();
    }
  }, [user, syncCart]);

  // Add item to cart
  const addToCart = useCallback(async (item: Omit<CartItem, 'id' | 'created_at' | 'updated_at'>) => {
    setError(null);

    if (isAuthenticated && user) {
      // Add to Supabase
      try {
        // Check if item already exists
        const { data: existing } = await supabase
          .from('carts')
          .select('*')
          .eq('user_id', user.id)
          .eq('product_id', item.product_id)
          .eq('size', item.size || '')
          .eq('color', item.color || '')
          .single();

        if (existing) {
          // Update quantity
          await updateQuantity(item.product_id, existing.quantity + item.quantity, item.size, item.color);
        } else {
          // Insert new item
          const { error: insertError } = await supabase
            .from('carts')
            .insert({
              user_id: user.id,
              product_id: item.product_id,
              quantity: item.quantity,
              size: item.size,
              color: item.color,
              custom_design: item.custom_design,
            });

          if (insertError) throw insertError;
          await fetchCart();
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
        setError(err instanceof Error ? err.message : 'Failed to add to cart');
        throw err;
      }
    } else {
      // Add to localStorage
      const localCart = loadLocalCart();
      const existingIndex = localCart.findIndex(
        i => i.product_id === item.product_id && i.size === item.size && i.color === item.color
      );

      if (existingIndex >= 0) {
        localCart[existingIndex].quantity += item.quantity;
      } else {
        localCart.push(item);
      }

      saveLocalCart(localCart);
      setCart(localCart);
    }
  }, [user, isAuthenticated, loadLocalCart, saveLocalCart, fetchCart]);

  // Remove item from cart
  const removeFromCart = useCallback(async (productId: string, size?: string, color?: string) => {
    setError(null);

    if (isAuthenticated && user) {
      // Remove from Supabase
      try {
        const { error: deleteError } = await supabase
          .from('carts')
          .delete()
          .eq('user_id', user.id)
          .eq('product_id', productId)
          .eq('size', size || '')
          .eq('color', color || '');

        if (deleteError) throw deleteError;
        await fetchCart();
      } catch (err) {
        console.error('Error removing from cart:', err);
        setError(err instanceof Error ? err.message : 'Failed to remove from cart');
        throw err;
      }
    } else {
      // Remove from localStorage
      const localCart = loadLocalCart();
      const filtered = localCart.filter(
        i => !(i.product_id === productId && i.size === size && i.color === color)
      );

      saveLocalCart(filtered);
      setCart(filtered);
    }
  }, [user, isAuthenticated, loadLocalCart, saveLocalCart, fetchCart]);

  // Update item quantity
  const updateQuantity = useCallback(async (productId: string, quantity: number, size?: string, color?: string) => {
    setError(null);

    if (quantity <= 0) {
      await removeFromCart(productId, size, color);
      return;
    }

    if (isAuthenticated && user) {
      // Update in Supabase
      try {
        const { error: updateError } = await supabase
          .from('carts')
          .update({ quantity })
          .eq('user_id', user.id)
          .eq('product_id', productId)
          .eq('size', size || '')
          .eq('color', color || '');

        if (updateError) throw updateError;
        await fetchCart();
      } catch (err) {
        console.error('Error updating quantity:', err);
        setError(err instanceof Error ? err.message : 'Failed to update quantity');
        throw err;
      }
    } else {
      // Update in localStorage
      const localCart = loadLocalCart();
      const index = localCart.findIndex(
        i => i.product_id === productId && i.size === size && i.color === color
      );

      if (index >= 0) {
        localCart[index].quantity = quantity;
        saveLocalCart(localCart);
        setCart(localCart);
      }
    }
  }, [user, isAuthenticated, loadLocalCart, saveLocalCart, fetchCart, removeFromCart]);

  // Clear entire cart
  const clearCart = useCallback(async () => {
    setError(null);

    if (isAuthenticated && user) {
      // Clear from Supabase
      try {
        const { error: deleteError } = await supabase
          .from('carts')
          .delete()
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;
        setCart([]);
      } catch (err) {
        console.error('Error clearing cart:', err);
        setError(err instanceof Error ? err.message : 'Failed to clear cart');
        throw err;
      }
    } else {
      // Clear from localStorage
      localStorage.removeItem(CART_STORAGE_KEY);
      setCart([]);
    }
  }, [user, isAuthenticated]);

  const cartCount = cart.reduce((total, item) => total + item.quantity, 0);

  return {
    cart,
    cartCount,
    loading,
    error,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    syncCart,
  };
}